// Connect control for the embedded bot wallet: log in with email → wallet
// auto-creates → the address becomes the button. Clicking the address opens
// WalletPopover (receive / QR / copy / export / log out).
//
// The two-step "Log out?" arm this button used to carry is GONE, and good
// riddance — it was a mitigation for a bad affordance, not a fix. The address
// IS the button, and people click an address to read it or copy it; wiring that
// instinct straight to logout() meant a stray click dropped the wallet, and the
// arm just made you click twice to have the accident. The popover removes the
// hazard instead of guarding it: the click now does what the instinct expects,
// and Log out is a labelled row inside a surface you opened on purpose.
//
// STYLING: reuses the app's canonical connect button (.local-wallet-btn + its
// .no-wallet / .locked / .connected state classes) instead of a private .privy-*
// look. That inherits the design language automatically — the diagonal chamfer
// (corner-shape: bevel), the currentColor state sliver, the Nova Square face,
// the shared --connect-accent — and any future restyle carries here for free:
//   logged out   → .no-wallet ("Log in", amber)
//   creating     → .locked    (amber, transient)
//   has address  → .connected (green, shows the short address)
import { useState, useRef, useEffect } from 'react'
import { useDualWallet } from './DualWalletContext'
import WalletPopover from './WalletPopover'

function shortAddr(a) {
  return a ? a.slice(0, 4) + '…' + a.slice(-4) : ''
}

export function PrivyWalletButton() {
  const { privyWallet, privyBooted, requestPrivyBoot, privyBlocked } = useDualWallet()
  const { ready, authenticated, address, login } = privyWallet

  const btnRef = useRef(null)
  const [open, setOpen] = useState(false)
  // Set when the user clicked "Log in" BEFORE the runtime existed. The click
  // ignites the boot; this remembers the intent so the login modal opens the
  // moment the runtime reports ready — one tap, not tap-wait-tap.
  const [pendingLogin, setPendingLogin] = useState(false)

  // Never leave the popover orphaned: if the session drops out from under us
  // there's no wallet left for it to be about.
  useEffect(() => { if (!address) setOpen(false) }, [address])

  useEffect(() => {
    if (!pendingLogin || !ready) return
    setPendingLogin(false)
    if (!authenticated) {
      try { login({ loginMethods: ['email'] }) } catch { /* blocked runtime — notice banner covers it */ }
    }
  }, [pendingLogin, ready, authenticated, login])

  // Runtime not booted (wallet mode isn't Privy, so nothing auto-loads it).
  // The button is ENABLED anyway: the tap is what boots it. Blocked runtime
  // keeps the old disabled face — booting again won't help.
  if (!privyBooted && !privyBlocked) {
    return (
      <button
        className="local-wallet-btn no-wallet"
        onClick={() => { setPendingLogin(true); requestPrivyBoot() }}
        title="Email login — creates your embedded bot wallet"
      >
        {pendingLogin ? '…' : 'Log in'}
      </button>
    )
  }

  if (!ready) {
    return <button className="local-wallet-btn no-wallet" disabled>…</button>
  }

  if (!authenticated) {
    return (
      <button
        className="local-wallet-btn no-wallet"
        onClick={() => login({ loginMethods: ['email'] })}
        title="Email login — creates your embedded bot wallet"
      >
        Log in
      </button>
    )
  }

  if (!address) {
    // Authenticated but the embedded wallet hasn't propagated yet.
    return <button className="local-wallet-btn locked" disabled>creating wallet…</button>
  }

  return (
    <>
      <button
        ref={btnRef}
        className="local-wallet-btn connected"
        onClick={() => setOpen(o => !o)}
        title={`${address}\nClick for wallet`}
      >
        {shortAddr(address)}
      </button>
      {open && (
        <WalletPopover anchorRef={btnRef} onClose={() => setOpen(false)} />
      )}
    </>
  )
}